/**
 * BrowseCatalogTab
 * 
 * Core feature: Browse all training artifacts with search/filter + server-side pagination.
 */

import { useState, useMemo, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, 
  Filter, 
  ExternalLink, 
  Clock,
  Loader2,
  BookOpen,
  CheckCircle2,
  ShoppingCart,
  Info,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { getTrainingArtifactAction } from '@/lib/trainingArtifactAction';
import { useRecommendedTraining, useTrackLearningClick } from '@/hooks/useLearningCenter';
import { useOpenTrainingWithSession } from '@/hooks/useOpenTrainingWithSession';
import { MobileTrainingFallbackLaunchInterstitial } from '@/components/training/MobileTrainingFallbackLaunchInterstitial';
import { 
  TrainingArtifact, 
  ARTIFACT_TYPE_LABELS, 
  ARTIFACT_TYPE_ICONS,
  ACCESS_TYPE_LABELS,
  SKILL_DEPTH_LABELS,
  CONFIDENCE_LEVEL_LABELS,
  type ArtifactType,
  type AccessType,
  type SkillDepth,
  type LearningIntent
} from '@/types/training';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 100;

interface BrowseCatalogTabProps {
  technicianProfileId: string;
  canOpen?: boolean;
}

export function BrowseCatalogTab({ technicianProfileId, canOpen = true }: BrowseCatalogTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<ArtifactType | 'all'>('all');
  const [accessFilter, setAccessFilter] = useState<AccessType | 'all'>('all');
  const [depthFilter, setDepthFilter] = useState<SkillDepth | 'all'>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);

  const { data: result, isLoading } = useRecommendedTraining({
    page: currentPage,
    pageSize: PAGE_SIZE,
  });

  const artifacts = result?.data ?? [];
  const total = result?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const trackClick = useTrackLearningClick();

  // Derive unique providers from current page for the filter dropdown
  const providerNames = useMemo(() => {
    const names = new Set<string>();
    artifacts.forEach(a => { if (a.provider_name) names.add(a.provider_name); });
    return Array.from(names).sort();
  }, [artifacts]);

  // Client-side filters applied to the current page
  const filteredArtifacts = useMemo(() => {
    return artifacts.filter((artifact) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          artifact.title.toLowerCase().includes(query) ||
          artifact.provider_name?.toLowerCase().includes(query) ||
          artifact.instructor_name?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      if (typeFilter !== 'all' && artifact.artifact_type !== typeFilter) return false;
      if (accessFilter !== 'all' && artifact.access_type !== accessFilter) return false;
      if (depthFilter !== 'all' && artifact.skill_depth !== depthFilter) return false;
      if (providerFilter !== 'all' && artifact.provider_name !== providerFilter) return false;
      return true;
    });
  }, [artifacts, searchQuery, typeFilter, accessFilter, depthFilter, providerFilter]);

  // Reset to page 1 when filters/search change
  const handleFilterChange = (setter: (v: any) => void) => (v: any) => {
    setter(v);
    setCurrentPage(1);
  };

  const { openTraining } = useOpenTrainingWithSession();

  // Batch 18-F11.13: opt-in mobile fallback affordance, keyed by
  // artifact ID. At most one interstitial is open at a time. Toggling
  // is purely UI state and does NOT call openTraining, does NOT call
  // window.open, does NOT create a session row. Launch happens only
  // when the user clicks the interstitial's own Open button, which
  // delegates to handleMobileFallbackOpen below.
  const [mobileFallbackArtifactId, setMobileFallbackArtifactId] = useState<
    string | null
  >(null);
  const toggleMobileFallback = useCallback((artifactId: string) => {
    setMobileFallbackArtifactId((prev) =>
      prev === artifactId ? null : artifactId,
    );
  }, []);

  // Batch 18-F11.13: fallback launch handler. Different from the
  // anchor-based handleArtifactClick below in ONE key way: it does
  // NOT pass `skipWindowOpen: true`, because the interstitial's Open
  // button is not an <a target="_blank"> — there is no native anchor
  // navigation to defer to. The hook therefore performs the single
  // window.open for this path. The trackClick + openTraining metadata
  // mirror the anchor path so the session row + 'opened' event look
  // identical regardless of which entrypoint the technician used.
  const handleMobileFallbackOpen = useCallback(
    (artifact: TrainingArtifact) => {
      trackClick.mutate({
        technicianProfileId,
        artifactId: artifact.id,
        source: 'learning_center',
        intent: 'curiosity' as LearningIntent,
      });
      const action = getTrainingArtifactAction(artifact);
      if (action.url) {
        openTraining({
          technicianProfileId,
          artifactId: artifact.id,
          artifactType: artifact.artifact_type,
          expectedDurationMinutes: artifact.estimated_time_minutes ?? null,
          surface: 'browse',
          source: 'learning_center',
          targetUrl: action.url,
          launchSource: 'browse_catalog',
        });
      }
    },
    [technicianProfileId, openTraining, trackClick],
  );

  const handleArtifactClick = (artifact: TrainingArtifact) => {
    trackClick.mutate({
      technicianProfileId,
      artifactId: artifact.id,
      source: 'learning_center',
      intent: 'curiosity' as LearningIntent,
    });
    // Batch 18-E6: route launch through the shared session hook so the
    // anchor navigation also produces a training_consumption_sessions
    // row + correlated 'opened' event. skipWindowOpen=true because the
    // <a target="_blank"> handles navigation natively.
    const action = getTrainingArtifactAction(artifact);
    if (action.url) {
      openTraining({
        technicianProfileId,
        artifactId: artifact.id,
        artifactType: artifact.artifact_type,
        expectedDurationMinutes: artifact.estimated_time_minutes ?? null,
        surface: 'browse',
        source: 'learning_center',
        targetUrl: action.url,
        launchSource: 'browse_catalog',
        skipWindowOpen: true,
      });
    }
  };

  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, total);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search training..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
          className="pl-10"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <Select value={typeFilter} onValueChange={handleFilterChange(setTypeFilter)}>
          <SelectTrigger className="w-[130px] h-9">
            <Filter className="h-3 w-3 mr-2" />
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(ARTIFACT_TYPE_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={accessFilter} onValueChange={handleFilterChange(setAccessFilter)}>
          <SelectTrigger className="w-[130px] h-9">
            <SelectValue placeholder="Access" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Access</SelectItem>
            {Object.entries(ACCESS_TYPE_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={depthFilter} onValueChange={handleFilterChange(setDepthFilter)}>
          <SelectTrigger className="w-[130px] h-9">
            <SelectValue placeholder="Skill Depth" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            {Object.entries(SKILL_DEPTH_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={providerFilter} onValueChange={handleFilterChange(setProviderFilter)}>
          <SelectTrigger className="w-[150px] h-9">
            <SelectValue placeholder="Provider" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Providers</SelectItem>
            {providerNames.map((name) => (
              <SelectItem key={name} value={name}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results count + page info */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {filteredArtifacts.length < artifacts.length
            ? `${filteredArtifacts.length} matching on this page`
            : `Showing ${startItem}–${endItem} of ${total} resources`
          }
        </p>
      </div>

      {/* Results */}
      <ScrollArea className="h-[400px]">
        {filteredArtifacts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>No training resources match your filters</p>
            <p className="text-sm mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="space-y-3 pr-4">
            {filteredArtifacts.map((artifact) => (
              <div 
                key={artifact.id}
                className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <span className="text-2xl flex-shrink-0">
                      {ARTIFACT_TYPE_ICONS[artifact.artifact_type]}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold text-base">{artifact.title}</h4>
                      <p className="text-sm text-muted-foreground">
                        {artifact.provider_name}
                        {artifact.instructor_name && ` • ${artifact.instructor_name}`}
                      </p>
                      
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <Badge variant="secondary" className="text-xs">
                          {ARTIFACT_TYPE_LABELS[artifact.artifact_type]}
                        </Badge>
                        <Badge 
                          variant={artifact.access_type === 'free' ? 'default' : 'outline'}
                          className={cn("text-xs", artifact.access_type === 'free' && "bg-green-600")}
                        >
                          {ACCESS_TYPE_LABELS[artifact.access_type]}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {SKILL_DEPTH_LABELS[artifact.skill_depth]}
                        </Badge>
                        {artifact.confidence_level === 'optibay_verified' && (
                          <Badge
                            variant="default"
                            className="text-xs bg-primary"
                            data-testid="browse-catalog-badge-optibay-reviewed-content"
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            OptiBay reviewed content
                          </Badge>
                        )}
                      </div>

                      {artifact.estimated_time_minutes && (
                        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {artifact.estimated_time_minutes < 60 
                            ? `${artifact.estimated_time_minutes} min`
                            : `${Math.round(artifact.estimated_time_minutes / 60 * 10) / 10} hrs`
                          }
                          {artifact.delivery_format === 'live' && ' • Live'}
                          {artifact.travel_required && ' • Travel Required'}
                        </p>
                      )}
                    </div>
                  </div>

                  {(() => {
                    if (canOpen === false) {
                      return (
                        <Badge variant="outline" className="text-xs text-muted-foreground flex-shrink-0">
                          View Only
                        </Badge>
                      );
                    }
                    
                    const action = getTrainingArtifactAction(artifact);
                    if (action.kind === 'disabled') {
                      return (
                        <Badge variant="outline" className="text-xs text-muted-foreground flex-shrink-0">
                          <Info className="h-3 w-3 mr-1" />
                          {action.label}
                        </Badge>
                      );
                    }
                    return (
                      <Button
                        size="sm"
                        onClick={() => handleArtifactClick(artifact)}
                        className="gap-1 flex-shrink-0"
                        asChild
                      >
                        <a href={action.url} target="_blank" rel="noopener noreferrer">
                          {action.kind === 'buy' && <ShoppingCart className="h-3 w-3" />}
                          {action.label}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </Button>
                    );
                  })()}
                </div>

                {/* Batch 18-F11.13: opt-in mobile fallback affordance.
                    Scoped to the actionable launch branch only — when
                    canOpen is false (View Only) or the action kind is
                    disabled (no URL), the toggle is hidden too. The
                    primary anchor above is unchanged; this is purely
                    secondary. The interstitial's onOpenProvider routes
                    through handleMobileFallbackOpen which DOES NOT use
                    skipWindowOpen=true (no anchor on this path). */}
                {canOpen !== false &&
                  getTrainingArtifactAction(artifact).kind !== 'disabled' &&
                  getTrainingArtifactAction(artifact).url && (
                    <div className="mt-3 pt-3 border-t space-y-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => toggleMobileFallback(artifact.id)}
                        data-testid={`browse-catalog-mobile-fallback-toggle-${artifact.id}`}
                      >
                        {mobileFallbackArtifactId === artifact.id
                          ? 'Hide mobile fallback'
                          : 'Open with mobile fallback'}
                      </Button>
                      {mobileFallbackArtifactId === artifact.id && (
                        <MobileTrainingFallbackLaunchInterstitial
                          trainingTitle={artifact.title}
                          providerName={artifact.provider_name}
                          providerUrl={
                            getTrainingArtifactAction(artifact).url ?? ''
                          }
                          estimatedDurationMinutes={
                            artifact.estimated_time_minutes ?? null
                          }
                          onOpenProvider={() =>
                            handleMobileFallbackOpen(artifact)
                          }
                          onCancel={() => setMobileFallbackArtifactId(null)}
                        />
                      )}
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>

          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="gap-1"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
